import { useCallback, useEffect, useMemo, useState } from "react";
import {
  editVendorDetailsById,
  getCitiesByStateVendor,
  getStateListVendor,
  getSubProductsByLob,
} from "../../Services/vendorManagement.service";
import useToaster from "../../customHook/useToaster";

import {
  emptyFormData,
  getHighValueOptions,
  getInitialFormData,
  getNextHighValue,
  getUpdatedSubProducts,
  getVendorLob,
  LOB,
  LOB_ID,
  lobPayloadMap,
  buildVendorUpdatePayload,
  validateVendorForm
} from "../../Utils/EditVendorModal.utils";

export const useEditVendorDetails = ({ vendorId, vendorDetails, onSaved }) => {
  const { showToast } = useToaster();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [fetchedSubProducts, setFetchedSubProducts] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  useEffect(() => {
    if (!vendorDetails) return;
    let cancelled = false;
    const initialize = async () => {
      try {
        const selectedLob = getVendorLob(vendorDetails);
        const lobId = LOB_ID[selectedLob];
        const [statesResponse, subProductsResponse] = await Promise.all([
          getStateListVendor(),
          lobId ? getSubProductsByLob(lobId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const states = statesResponse?.success ? statesResponse.data || [] : [];
        const subProducts = subProductsResponse?.success
          ? subProductsResponse.data || []
          : [];
        setStateOptions(states);
        setFetchedSubProducts(subProducts);
        const selectedState = states.find(
          (option) =>
            option?.state_name?.toLowerCase() ===
            (vendorDetails?.state || "").toLowerCase()
        );
        let cities = [];
        if (selectedState?.state_id) {
          const citiesResponse = await getCitiesByStateVendor(
            selectedState.state_id
          );
          if (citiesResponse?.success) {
            cities = citiesResponse.data || [];
          }
        }
        if (cancelled) return;
        setCityOptions(cities);
       const safeVendorDetails = {
          ...vendorDetails,
          sub_product_id: vendorDetails.sub_product_id || [] 
        };
        setFormData(
          getInitialFormData({
            vendorDetails: safeVendorDetails,
            selectedLob,
            subProducts,
          })
        );
      } catch (error) {
        if (cancelled) return;
        showToast(
          error?.response?.data?.message ||
            "Unable to initialize vendor details.",
          "error",
          true
        );
      }
    };
    initialize();
    return () => {
      cancelled = true;
    };
  }, [vendorDetails]);

  const clearFieldError = useCallback((name) => {
    setFormErrors((previous) => {
      if (!previous[name]) return previous;
      const next = { ...previous };
      delete next[name];
      return next;
    });
  }, []);

  const clearFieldErrors = useCallback((names) => {
    setFormErrors((previous) => {
      const next = { ...previous };
      names.forEach((name) => {
        delete next[name];
      });
      return next;
    });
  }, []);

  const handleFieldChange = useCallback(
    (event) => {
      let { name, value } = event.target;
      if (name === "gc_code") return;
      if (name === "pan_number" || name === "gst_number") {
        value = value.toUpperCase();
      }
      clearFieldError(name);
      setFormData((previous) => ({
        ...previous,
        [name]: value,
      }));
    },
    [clearFieldError]
  );

  const handleStateChange = useCallback(
    async (event) => {
      const value = event.target.value;
      const selectedState = stateOptions.find(
        (option) => option?.state_name?.toLowerCase() === value?.toLowerCase()
      );
      clearFieldErrors(["state", "city"]);
      setFormData((previous) => ({
        ...previous,
        state: value,
        city: "",
      }));
      if (!selectedState?.state_id) {
        setCityOptions([]);
        return;
      }
      try {
        const response = await getCitiesByStateVendor(selectedState.state_id);
        setCityOptions(response?.success ? response.data || [] : []);
      } catch (error) {
        setCityOptions([]);
        showToast(
          error?.response?.data?.message || "Unable to load cities.",
          "error",
          true
        );
      }
    },
    [stateOptions, clearFieldErrors, showToast]
  );

  const handleLobChange = useCallback(
    async (event) => {
      const value = event.target.value;
      const lobId = LOB_ID[value];
      let nextAvailableOptions = [];
      if (lobId) {
        try {
          const response = await getSubProductsByLob(lobId);
          if (response?.success) {
            const subProducts = response.data || [];
            setFetchedSubProducts(subProducts);
            nextAvailableOptions = subProducts.map(
              (item) => item.sub_product_name
            );
          }
        } catch (error) {
          setFetchedSubProducts([]);
          showToast(
            error?.response?.data?.message || "Unable to load sub products.",
            "error",
            true
          );
        }
      } else {
        setFetchedSubProducts([]);
      }
      clearFieldError("lob_id");
      clearFieldErrors([
        "capacity_motor",
        "capacity_health",
        "sub_product_id",
        "high_value",
      ]);
      setFormData((previous) => ({
        ...previous,
        lob_id: value,
        sub_product_id: getUpdatedSubProducts({
          value,
          previousSubProducts: previous.sub_product_id,
          availableOptions: nextAvailableOptions,
        }),
        high_value: [],
      }));
    },
    [clearFieldError, clearFieldErrors, showToast]
  );

  const handleSpecialisedChange = useCallback(
    (event) => {
      const value = event.target.value;
      const normalizedValue =
        value === "Yes" ? "yes" : value === "No" ? "no" : "";
      clearFieldError("specialised");
      setFormData((previous) => ({
        ...previous,
        specialised: normalizedValue,
      }));
    },
    [clearFieldError]
  );

  const handleSubProductChange = useCallback(
    (event) => {
      const value = event.target.value;
      const nextValue = Array.isArray(value) ? value : [value];
      clearFieldError("sub_product_id");
      clearFieldError("high_value");
      const availableHighValues = getHighValueOptions({
        lob: formData.lob_id,
        subProducts: nextValue,
      });
      setFormData((previous) => ({
        ...previous,
        sub_product_id: nextValue,
        high_value: previous.high_value.filter((value) =>
          availableHighValues.includes(value)
        ),
      }));
    },
    [formData.lob_id, clearFieldError]
  );

  const handleHighValueChange = useCallback(
    (event, availableOptions) => {
      const rawValue = event.target.value;
      clearFieldError("high_value");
      setFormData((previous) => ({
        ...previous,
        high_value: getNextHighValue({
          rawValue,
          availableOptions,
          previousValue: previous.high_value,
        }),
      }));
    },
    [clearFieldError]
  );

  const availableSubProductOptions = useMemo(
    () =>
      formData.lob_id === LOB.MOTOR
        ? fetchedSubProducts.map((item) => item.sub_product_name)
        : [],
    [formData.lob_id, fetchedSubProducts]
  );

  const highValueOptions = useMemo(
    () =>
      getHighValueOptions({
        lob: formData.lob_id,
        subProducts: formData.sub_product_id,
      }),
    [formData.lob_id, formData.sub_product_id]
  );

  const handleSave = useCallback(async () => {
    const activeErrors = validateVendorForm(formData);
    if (Object.keys(activeErrors).length > 0) {
      setFormErrors(activeErrors);
      showToast("Please enter correct fields before saving.", "error", true);
      return;
    }
    try {
      setIsSaving(true);
      const payload = buildVendorUpdatePayload({
        vendorId,
        formData,
        fetchedSubProducts,
        lobPayloadMap,
      });
      const response = await editVendorDetailsById(payload);
      if (!response?.success) {
        showToast(
          response?.message || "Unable to update vendor details.",
          "error",
          true
        );
        return;
      }
      showToast(
        response?.message || "Vendor details updated successfully.",
        "success",
        true
      );
      await onSaved?.();
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Unable to update vendor details.",
        "error",
        true
      );
    } finally {
      setIsSaving(false);
    }
  }, [vendorId, formData, fetchedSubProducts, onSaved, showToast]);
  return {
    formData,
    formErrors,
    stateOptions,
    cityOptions,
    availableSubProductOptions,
    highValueOptions,
    isSaving,
    handleFieldChange,
    handleStateChange,
    handleSpecialisedChange,
    handleLobChange,
    handleSubProductChange,
    handleHighValueChange,
    handleSave,
  };
};
